import React, {useCallback, useEffect, useRef, useState} from "react";
import {AuthProvider, useAuth} from "react-oidc-context";
import {WebStorageStateStore} from "oidc-client-ts";
import PropTypes from "prop-types";
import {setCookieSession, clearCookieSession} from "./cookies.js";

const RENEW_RETRY_DELAYS_MS = [5000, 15000, 60000];

const useReauthenticate = (auth, inFlightRef, stopRecovery) => {
  return useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    stopRecovery();
    try {
      clearCookieSession();
      await auth.removeUser();
      await auth.signinRedirect();
    } finally {
      // Reset so a blocked or failed redirect doesn't wedge re-auth permanently.
      inFlightRef.current = false;
    }
  }, [auth, inFlightRef, stopRecovery]);
};

// Two things here resist simplification: the subscription must hang off auth.events, which
// outlives the re-render react-oidc-context triggers on silentRenewError, and attempts must
// re-schedule from their own result, since that event fires once per frozen session.
const useSilentRenewRecovery = (auth, onExhausted) => {
  const signinSilentRef = useRef(auth?.signinSilent);
  const onExhaustedRef = useRef(onExhausted);
  signinSilentRef.current = auth?.signinSilent;
  onExhaustedRef.current = onExhausted;

  const timerRef = useRef(null);
  const attemptRef = useRef(0);
  const activeRef = useRef(false);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = null;
    attemptRef.current = 0;
    activeRef.current = false;
  }, []);

  const attempt = useCallback(async () => {
    timerRef.current = null;

    const renewed = await signinSilentRef.current?.();
    if (renewed) {
      stop();
      return;
    }

    const delay = RENEW_RETRY_DELAYS_MS[attemptRef.current];
    if (delay === undefined) {
      stop();
      onExhaustedRef.current?.();
      return;
    }

    attemptRef.current += 1;
    timerRef.current = setTimeout(attempt, delay);
  }, [stop]);

  const start = useCallback(() => {
    if (activeRef.current) {
      return;
    }
    activeRef.current = true;
    attemptRef.current = 1;
    timerRef.current = setTimeout(attempt, RENEW_RETRY_DELAYS_MS[0]);
  }, [attempt]);

  useEffect(() => {
    const events = auth?.events;
    if (!events?.addSilentRenewError) {
      return;
    }

    const removeError = events.addSilentRenewError(start);
    const removeLoaded = events.addUserLoaded?.(stop);

    return () => {
      removeError?.();
      removeLoaded?.();
    };
  }, [auth?.events, start, stop]);

  useEffect(() => stop, [stop]);

  return {start, stop};
};

const AuthGuard = ({children, LoaderComponent, recoveryRef, reauthRef, reauthInFlightRef}) => {
  const auth = useAuth();
  const {start: startRecovery, stop: stopRecovery} = useSilentRenewRecovery(auth, () =>
    reauthRef?.current?.(),
  );
  const reauthenticate = useReauthenticate(auth, reauthInFlightRef, stopRecovery);

  if (recoveryRef) {
    recoveryRef.current = startRecovery;
  }
  if (reauthRef) {
    reauthRef.current = reauthenticate;
  }

  // removeUser leaves isLoading untouched, so without the guard this fires its own redirect
  // whenever a render lands between removeUser resolving and signinRedirect dispatching.
  useEffect(() => {
    if (!auth.isLoading && !auth.user && !reauthInFlightRef.current) {
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.user, auth.signinRedirect]);

  // Gating on isLoading too would unmount the tree during a renew, killing a live meeting.
  if (!auth.user) {
    return LoaderComponent || null;
  }

  return children;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
  LoaderComponent: PropTypes.node,
  recoveryRef: PropTypes.object,
  reauthRef: PropTypes.object,
  reauthInFlightRef: PropTypes.object,
};

const AuthAdapter = ({Context, children, recoveryRef, reauthRef}) => {
  const auth = useAuth();
  const [authSession, setAuthSession] = useState(null);
  const orgName = window.location.hostname.split(".").shift();

  useEffect(() => {
    if (auth.user) {
      setAuthSession((prev) => {
        if (prev?.AccessToken === auth.user.access_token) {
          return prev;
        }
        const session = {
          AccessToken: auth.user.access_token,
          IdToken: auth.user.id_token,
          RefreshToken: auth.user.refresh_token,
          username: auth.user.profile.preferred_username || auth.user.profile.email,
          orgName,
          ...auth.user.profile,
        };
        setCookieSession(session);
        return session;
      });
    }
  }, [auth.user, orgName]);

  const contextValue = {
    authSession,
    isAuthLoading: auth.isLoading,
    errorMessage: auth.error?.message || "",
    login: async (args) => {
      await auth.signinRedirect(args);
    },
    logout: async () => {
      clearCookieSession();
      await auth.signoutRedirect();
    },
    // Redirecting here would tear down whatever the caller is mid-way through, and no
    // silentRenewError is raised for a renewal we request ourselves — hence the explicit
    // hand-off to the recovery chain, which owns escalation.
    refreshSession: async () => {
      try {
        const refreshed = await auth.signinSilent();
        if (refreshed?.id_token) {
          return {
            AccessToken: refreshed.access_token,
            IdToken: refreshed.id_token,
            RefreshToken: refreshed.refresh_token,
          };
        }
      } catch {
        // react-oidc-context resolves null rather than throwing; treated the same.
      }

      recoveryRef?.current?.();
      return null;
    },
    reauthenticate: () => reauthRef?.current?.(),
    setErrorMessage: () => {},
    setAuthSession: () => {},
    orgName,
    hasAuthProviders: false,
    altAuthProviders: [],
    resetAuthProviders: () => {},
    isAltAuthLoading: false,
    setLoginComponent: () => {},
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

AuthAdapter.propTypes = {
  Context: PropTypes.object.isRequired,
  children: PropTypes.node,
  recoveryRef: PropTypes.object,
  reauthRef: PropTypes.object,
};

export const BreachRxAuthProvider = ({config, Context, LoaderComponent, children}) => {
  const recoveryRef = useRef(null);
  const reauthRef = useRef(null);
  const reauthInFlightRef = useRef(false);

  const oidcConfig = {
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 90,
    ...config,
    userStore: new WebStorageStateStore({store: window.localStorage}),
    onSigninCallback: (user) => {
      window.history.replaceState({}, document.title, window.location.pathname);
      if (config.onSigninCallback) {
        config.onSigninCallback(user);
      }
    },
  };

  const content =
    Context ?
      (
        <AuthAdapter Context={Context} recoveryRef={recoveryRef} reauthRef={reauthRef}>
          {children}
        </AuthAdapter>
      ) :
      children;

  return (
    <AuthProvider {...oidcConfig}>
      <AuthGuard
        LoaderComponent={LoaderComponent}
        recoveryRef={recoveryRef}
        reauthRef={reauthRef}
        reauthInFlightRef={reauthInFlightRef}
      >
        {content}
      </AuthGuard>
    </AuthProvider>
  );
};

BreachRxAuthProvider.propTypes = {
  config: PropTypes.object.isRequired,
  Context: PropTypes.object,
  LoaderComponent: PropTypes.node,
  children: PropTypes.node,
};
